/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AbsenceMarkReturned } from '../models/AbsenceMarkReturned';
import type { AbsenceModel } from '../models/AbsenceModel';
import type { AbsenceWebhookUpsertResponse } from '../models/AbsenceWebhookUpsertResponse';
import type { LiveActivityToken } from '../models/LiveActivityToken';
import type { PatchedAbsenceModel } from '../models/PatchedAbsenceModel';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AbsenceService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @returns AbsenceModel
     * @throws ApiError
     */
    public absenceRetrieve({
        patientId,
    }: {
        patientId?: number,
    }): CancelablePromise<AbsenceModel> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/absence/',
            query: {
                'patient_id': patientId,
            },
        });
    }

    /**
     * Create or update Live Activity token for an absence
     * @returns LiveActivityToken
     * @throws ApiError
     */
    public absenceLiveActivityTokenCreate({
        absenceId,
        requestBody,
    }: {
        absenceId: number,
        requestBody: LiveActivityToken,
    }): CancelablePromise<LiveActivityToken> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/absence/{absence_id}/live-activity-token/',
            path: {
                'absence_id': absenceId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * ViewSet for managing Live Activity tokens
     * @returns void
     * @throws ApiError
     */
    public absenceLiveActivityTokenDestroy({
        absenceId,
    }: {
        absenceId: number,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/absence/{absence_id}/live-activity-token/',
            path: {
                'absence_id': absenceId,
            },
        });
    }

    /**
     * Mark patient as returned from outing
     * Marks the patient as returned from their current outing. Requires a valid TOTP code for verification.
     * @returns void
     * @throws ApiError
     */
    public absenceMarkReturnedCreate({
        patientId,
        requestBody,
    }: {
        patientId?: number,
        requestBody?: AbsenceMarkReturned,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/absence/mark-returned/',
            query: {
                'patient_id': patientId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns AbsenceModel
     * @throws ApiError
     */
    public absenceProlongUpdate({
        requestBody,
        patientId,
    }: {
        requestBody: AbsenceModel,
        patientId?: number,
    }): CancelablePromise<AbsenceModel> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/absence/prolong/',
            query: {
                'patient_id': patientId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns AbsenceModel
     * @throws ApiError
     */
    public absenceProlongPartialUpdate({
        patientId,
        requestBody,
    }: {
        patientId?: number,
        requestBody?: PatchedAbsenceModel,
    }): CancelablePromise<AbsenceModel> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/absence/prolong/',
            query: {
                'patient_id': patientId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns AbsenceModel
     * @throws ApiError
     */
    public absenceRegisterCreate({
        requestBody,
        patientId,
    }: {
        requestBody: AbsenceModel,
        patientId?: number,
    }): CancelablePromise<AbsenceModel> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/absence/register/',
            query: {
                'patient_id': patientId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Process absence module webhook events (create/update/delete).
     *
     * Expected payloads (examples):
     * {
         * "event": "create" | "update",
         * "data": {
             * "patientId": 310788861,
             * "wardId": 217,
             * "eocId": 320352101,
             * "clinicId": 1,
             * "absenceLevel": 4
             * }
             * }
             *
             * {
                 * "event": "delete",
                 * "data": { "eocId": 320352102, "clinicId": 1 }
                 * }
                 * @returns AbsenceWebhookUpsertResponse
                 * @throws ApiError
                 */
                public absenceWebhookCreate(): CancelablePromise<AbsenceWebhookUpsertResponse> {
                    return this.httpRequest.request({
                        method: 'POST',
                        url: '/api/absence/webhook/',
                        errors: {
                            400: `No response body`,
                        },
                    });
                }

                /**
                 * @returns void
                 * @throws ApiError
                 */
                public absenceWebhookDestroy(): CancelablePromise<void> {
                    return this.httpRequest.request({
                        method: 'DELETE',
                        url: '/api/absence/webhook/',
                    });
                }

            }
